import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { storage } from '../../firebase-config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import axios from 'axios';
import API_BASE_URL from '../../utils/apiConfig';

// Dynamic dropdown lists
const POST_TYPE_OPTIONS = [
    'India',
    'Andhra Pradesh',
    'Telangana',
    'Special Day Wishes',
];
const STATE_OPTIONS = ['Andhra Pradesh', 'Telangana'];
const DISTRICT_OPTIONS = ['Guntur', 'Krishna', 'Hyderabad', 'Warangal', 'Visakhapatnam']; 
const CONSTITUENCY_OPTIONS = ['Constituency A', 'Constituency B', 'Constituency C'];
const MANDAL_OPTIONS = ['Mandal X', 'Mandal Y', 'Mandal Z'];

// Reusable Multi-Select Component
const MultiSelect = ({ label, options, selected, onChange }) => (
    <div className="form-group">
        <label>{label}</label>
        <select 
            multiple 
            value={selected} 
            onChange={(e) => onChange(Array.from(e.target.selectedOptions, option => option.value))} 
            style={{ minHeight: '100px', padding: '10px', width: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
        >
            {options.map(option => (
                <option key={option} value={option}>{option}</option>
            ))}
        </select>
        <p className="select-hint" style={{fontSize: '0.8em', color: '#666'}}>Hold Ctrl/Cmd to select multiple. Optional.</p>
    </div>
);

const CreateAdModal = ({ onClose, onAdCreated, initialData }) => {
    const { currentUser, firebaseUser } = useAuth();
    const isEditing = !!initialData?.id; 

    // --- STATE INITIALIZATION ---
    const [adType, setAdType] = useState(initialData?.adType || 'text'); 
    const [brandName, setBrandName] = useState(initialData?.sponsor || initialData?.adDetails?.brandName || '');
    const [adText, setAdText] = useState(initialData?.adText || initialData?.adDetails?.adText || '');
    const [linkUrl, setLinkUrl] = useState(initialData?.link || initialData?.adDetails?.linkUrl || '');
    const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
    const [imageFiles, setImageFiles] = useState([]); 
    const [uploadedImageUrls, setUploadedImageUrls] = useState(initialData?.imageUrls || []);
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState(initialData?.videoUrl || '');
    const [layoutType, setLayoutType] = useState(initialData?.layoutType || initialData?.adDetails?.layoutType || 'full_card');

    // Targeting states
    const [selectedPostTypes, setSelectedPostTypes] = useState(initialData?.targetPostTypes || []);
    const [selectedStates, setSelectedStates] = useState(initialData?.targetStates || []);
    const [selectedDistricts, setSelectedDistricts] = useState(initialData?.targetDistricts || []);
    const [selectedConstituencies, setSelectedConstituencies] = useState(initialData?.targetConstituencies || []);
    const [selectedMandals, setSelectedMandals] = useState(initialData?.targetMandals || []);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Dynamic price calculation
    const totalPrice = useMemo(() => {
        let count = 0;
        if (selectedPostTypes.includes('World')) count += 1;
        if (selectedPostTypes.includes('India')) count += 1;

        const hasStateSelected = selectedPostTypes.some(t => t === 'Andhra Pradesh' || t === 'Telangana');
        if (hasStateSelected) {
            if (selectedMandals.length > 0) {
                count += selectedMandals.length; 
            } else if (selectedConstituencies.length > 0) {
                count += selectedConstituencies.length; 
            } else if (selectedDistricts.length > 0) {
                count += selectedDistricts.length; 
            } else {
                count += 1; 
            }
        }
        return count * 100;
    }, [selectedPostTypes, selectedDistricts, selectedConstituencies, selectedMandals]);

    // Handle single/multiple upload files
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (mediaType === 'image' && files.length > 4) {
            alert("Maximum 4 images allowed.");
            return;
        }
        if (mediaType === 'video') {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                if (video.duration > 240) {
                    alert("Video must be 4 minutes or less.");
                    setImageFiles([]);
                }
            };
            video.src = URL.createObjectURL(files[0]);
        }
        setImageFiles(files);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!adText && imageFiles.length === 0 && !uploadedImageUrls.length && !uploadedVideoUrl) {
            setError("You must provide either Ad Text/Slogan or upload an Ad Media.");
            setLoading(false);
            return;
        }
        if (selectedPostTypes.length === 0) {
            setError("Please select at least one Post Type where your ad should appear.");
            setLoading(false);
            return;
        }

        try {
            let finalImageUrls = [...uploadedImageUrls];
            let finalVideoUrl = uploadedVideoUrl;

            if (imageFiles.length > 0) {
                const uploadPromises = imageFiles.map(async (file) => {
                    const storageRef = ref(storage, `advertisements/${Date.now()}_${file.name}`);
                    await uploadBytes(storageRef, file);
                    return await getDownloadURL(storageRef);
                });

                const newUrls = await Promise.all(uploadPromises);
                
                if (mediaType === 'video') {
                    finalVideoUrl = newUrls[0]; 
                } else {
                    finalImageUrls = isEditing ? [...finalImageUrls, ...newUrls] : newUrls;
                }
            }

            let finalPostTypes = [...selectedPostTypes];
            const isLocallyTargeted = selectedDistricts.length > 0 || selectedConstituencies.length > 0 || selectedMandals.length > 0;
            if (isLocallyTargeted) {
                finalPostTypes = finalPostTypes.filter(type => type !== 'Andhra Pradesh' && type !== 'Telangana');
            }

            const targetingPayload = { 
                postTypes: finalPostTypes, 
                states: isLocallyTargeted ? [] : selectedPostTypes.filter(t => t === 'Andhra Pradesh' || t === 'Telangana'),
                districts: selectedDistricts,
                constituencies: selectedConstituencies,
                mandals: selectedMandals
            };

            const adDetails = { 
                linkUrl, 
                adText, 
                brandName, 
                uploadedImageUrls: finalImageUrls, 
                uploadedImageUrl: finalVideoUrl, 
                mediaType,
                layoutType
            };

            const activeUser = firebaseUser || currentUser;
            if (!activeUser) {
                setError("User session not found. Please log in again.");
                setLoading(false);
                return;
            }
            const idToken = await activeUser.getIdToken(true); 

            if (isEditing) {
                await axios.put(`${API_BASE_URL}/ads/${initialData.id}`, { ...adDetails, targeting: targetingPayload }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
            } else {
                const orderRes = await axios.post(`${API_BASE_URL}/ads/create-order`, { amount: totalPrice }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });

                const paymentDetails = {
                    razorpay_order_id: orderRes.data.id,
                    razorpay_payment_id: "test_pay_" + Date.now(), 
                    razorpay_signature: "test_signature_" + Date.now(), 
                };

                await axios.post(`${API_BASE_URL}/ads/verify-payment`, {
                    paymentDetails,
                    adDetails,
                    targeting: targetingPayload,
                    mediaType: mediaType
                }, { headers: { Authorization: `Bearer ${idToken}` } });
            }
            onClose(); 
            if (onAdCreated) onAdCreated(); 
        } catch (err) {
            setError(`Error: ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Post New Advertisement</h2>
                    <button onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    {error && <p className="error-message" style={{color: 'red'}}>{error}</p>}
                    <form onSubmit={handleSubmit}>

                        {/* Ad Type Selector */}
                        <div className="form-group">
                            <label>Ad Slogan Option</label>
                            <select value={adType} onChange={(e) => setAdType(e.target.value)}>
                                <option value="text">Text Slogan (Custom Overlay on Image)</option>
                                <option value="image">Media Only (Display Uploaded Media Directly)</option>
                            </select>
                        </div>

                        {/* Shared Fields */}
                        <div className="form-group">
                            <label>Brand/Sponsor Name</label>
                            <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g. My Brand" required />
                        </div>
                        
                        <div className="form-group">
                            <label>Destination Link (URL)</label>
                            <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://mybrand.com/offer" required />
                        </div>

                        {/* Ad Slogan Text */}
                        {adType === 'text' && (
                            <div className="form-group">
                                <label>Ad Text/Slogan</label>
                                <textarea
                                    value={adText}
                                    onChange={(e) => setAdText(e.target.value)}
                                    placeholder="e.g. Get 50% off on all products for a limited time!"
                                    required
                                />
                                <p className="select-hint">The system will display this slogan on the bottom area of your ad.</p>
                            </div>
                        )}

                        {/* --- Ad Layout Selector --- */}
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Ad Card Layout Format</label>
                            <div className="layout-type-selection" style={{ display: 'flex', gap: '15px' }}>
                                
                                <label 
                                    className={`layout-option-item ${layoutType === 'full_card' ? 'selected' : ''}`}
                                    style={{
                                        flex: 1,
                                        border: layoutType === 'full_card' ? '2px solid var(--primary-color)' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: layoutType === 'full_card' ? 'rgba(255, 152, 0, 0.05)' : '#fff',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center'
                                    }}
                                >
                                    <input 
                                        type="radio" 
                                        name="layoutType" 
                                        value="full_card" 
                                        checked={layoutType === 'full_card'} 
                                        onChange={() => setLayoutType('full_card')}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <strong style={{ display: 'block', fontSize: '13px' }}>నిలువు ప్రకటన (Vertical Ad - 4:5 Ratio)</strong>
                                </label>

                                <label 
                                    className={`layout-option-item ${layoutType === 'banner' ? 'selected' : ''}`}
                                    style={{
                                        flex: 1,
                                        border: layoutType === 'banner' ? '2px solid var(--primary-color)' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: layoutType === 'banner' ? 'rgba(255, 152, 0, 0.05)' : '#fff',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center'
                                    }}
                                >
                                    <input 
                                        type="radio" 
                                        name="layoutType" 
                                        value="banner" 
                                        checked={layoutType === 'banner'} 
                                        onChange={() => setLayoutType('banner')}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <strong style={{ display: 'block', fontSize: '13px' }}>అడ్డ ప్రకటన (Horizontal Ad - 16:9 Ratio)</strong>
                                </label>

                            </div>
                        </div>

                        {/* Media Category (Image vs Video) */}
                        <div className="media-toggle" style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                            <button 
                                type="button" 
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: mediaType === 'image' ? '#ff9800' : '#ccc',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => { setMediaType('image'); setImageFiles([]); }}
                            >
                                Images (Max 4)
                            </button>
                            <button 
                                type="button" 
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: mediaType === 'video' ? '#ff9800' : '#ccc',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => { setMediaType('video'); setImageFiles([]); }}
                            >
                                Video (Max 4 min)
                            </button>
                        </div>

                        {/* File Upload Field */}
                        <div className="form-group">
                            <label>Upload Media Files</label>
                            <input 
                                type="file" 
                                accept={mediaType === 'video' ? "video/*" : "image/*"} 
                                multiple={mediaType === 'image'} 
                                onChange={handleFileChange} 
                                required={imageFiles.length === 0 && !uploadedImageUrls.length && !uploadedVideoUrl}
                            />
                            <p className="limit-hint" style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
                                {mediaType === 'video' 
                                    ? "⚠️ Video limit: Maximum 1 video, up to 4 minutes." 
                                    : "⚠️ Image limit: Maximum 4 images."}
                            </p>
                        </div>
                        
                        <h3 style={{marginTop: '20px'}}>Targeting Options</h3>
                        <MultiSelect 
                            label="Target Post Types (REQUIRED)"
                            options={POST_TYPE_OPTIONS}
                            selected={selectedPostTypes}
                            onChange={setSelectedPostTypes}
                        />
                        <MultiSelect 
                            label="Target States (Optional)"
                            options={STATE_OPTIONS}
                            selected={selectedStates}
                            onChange={setSelectedStates}
                        />
                        <MultiSelect 
                            label="Target Districts (Optional)"
                            options={DISTRICT_OPTIONS}
                            selected={selectedDistricts}
                            onChange={setSelectedDistricts}
                        />
                        <MultiSelect 
                            label="Target Constituencies (Optional)"
                            options={CONSTITUENCY_OPTIONS}
                            selected={selectedConstituencies}
                            onChange={setSelectedConstituencies}
                        />
                        <MultiSelect 
                            label="Target Mandals (Optional)"
                            options={MANDAL_OPTIONS}
                            selected={selectedMandals}
                            onChange={setSelectedMandals}
                        />

                        <div className="modal-footer" style={{marginTop: '20px'}}>
                            <div className="price-display" style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#ff9800', margin: '15px 0'}}>
                                Total Amount: ₹{totalPrice}
                            </div>
                            <button type="button" className="button-secondary" onClick={onClose}>Cancel</button>
                            <button type="submit" className="button-primary" disabled={loading}>
                                {loading ? 'Processing...' : `Pay ₹${totalPrice} & Post`}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateAdModal;